import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Users, MapPin, Calendar, DollarSign, Clock, Search, Filter,
  CheckCircle2, Eye, AlertCircle, Loader2, ChevronRight, Briefcase,
  Phone, Mail, MessageSquare, ArrowRight, Inbox, Bell, Sparkles,
  X, Send, User, FileText, Info
} from "lucide-react";

// Time ago helper
const timeAgo = (dateString) => {
  if (!dateString) return "Recently";
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    new: { bg: "bg-blue-100", text: "text-blue-700", label: "New" },
    assigned: { bg: "bg-purple-100", text: "text-purple-700", label: "Assigned" },
    viewed: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Viewed" },
    accepted: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Accepted" },
    expired: { bg: "bg-gray-100", text: "text-gray-500", label: "Expired" },
    open: { bg: "bg-blue-100", text: "text-blue-700", label: "Available" },
    responded: { bg: "bg-green-100", text: "text-green-700", label: "Responded" },
  };
  
  const style = styles[status] || styles.new;
  
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
};

// Lead Card Component
const LeadCard = ({ lead, onAccept, onView, isAccepted, loading }) => {
  const job = lead.job || lead;
  const assignment = lead.job ? lead : null;
  
  return (
    <Card 
      className="bg-white border border-slate-200 hover:border-[#d4a853]/50 hover:shadow-lg transition-all duration-200 cursor-pointer"
      onClick={() => onView(lead)}
      data-testid={`lead-card-${job.id || lead.id}`}
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-[#1e3a5f] text-lg truncate mb-1">
              {job.title || job.service_category || "Service Request"}
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center px-2 py-0.5 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded text-xs font-medium">
                {job.service_category || job.category || "General"}
              </span>
              <StatusBadge status={assignment?.status || job.status || "new"} />
            </div>
          </div>
        </div>
        
        {/* Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <MapPin className="w-4 h-4 text-slate-400" />
            <span>{job.city || job.location || "New Jersey"}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span>{timeAgo(job.created_at || assignment?.assigned_at)}</span>
          </div>
          
          {(job.budget || job.target_price || job.budget_max) && (
            <div className="flex items-center gap-2 text-sm">
              <DollarSign className="w-4 h-4 text-[#d4a853]" />
              <span className="font-semibold text-[#d4a853]">
                ${job.budget || job.target_price || `${job.budget_min || 0} - ${job.budget_max}`}
              </span>
            </div>
          )}
        </div>
        
        {/* Description */}
        {job.description && (
          <p className="text-sm text-slate-600 mb-4 line-clamp-2">
            {job.description}
          </p>
        )}
        
        {/* Customer Info (if accepted) */}
        {isAccepted && job.customer_name && (
          <div className="bg-emerald-50 rounded-lg p-3 mb-4">
            <p className="text-xs font-semibold text-emerald-700 mb-2">Customer Contact:</p>
            <div className="space-y-1">
              <p className="text-sm text-emerald-900 font-medium">{job.customer_name}</p>
              {job.customer_phone && (
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <Phone className="w-3 h-3" />
                  {job.customer_phone}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(lead)}
            className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50"
            data-testid={`view-lead-${job.id || lead.id}`}
          >
            <Eye className="w-4 h-4 mr-1" />
            View Details
          </Button>
          
          {!isAccepted && (
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onAccept(job); }}
              disabled={loading}
              className="flex-1 bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f] font-semibold"
              data-testid={`accept-lead-${job.id || lead.id}`}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Accept Lead
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Lead Detail Modal Component
const LeadDetailModal = ({ lead, onClose, onAccept, onRespond, isAccepted, loading }) => {
  const job = lead?.job || lead;
  const [bidAmount, setBidAmount] = useState("");
  const [bidMessage, setBidMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { token } = useAuth();
  
  if (!lead) return null;
  
  const handleSubmitBid = async () => {
    if (!bidAmount || !bidMessage) {
      toast.error("Please enter both a bid amount and message");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/jobs/${job.id}/bids`,
        {
          amount: parseFloat(bidAmount),
          message: bidMessage,
          estimated_duration: "To be discussed"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Bid submitted successfully!");
      onRespond && onRespond();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit bid");
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-detail-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-[#1e3a5f]">
              {job.title || job.service_category || "Service Request"}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={lead.status || job.status || "new"} />
              <span className="text-sm text-slate-500">{timeAgo(job.created_at)}</span>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            data-testid="close-modal"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Service Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-[#1e3a5f]" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Service Category</p>
                  <p className="font-semibold text-[#1e3a5f]">{job.service_category || job.category || "General"}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#1e3a5f]" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Location</p>
                  <p className="font-semibold text-[#1e3a5f]">{job.location || job.city || job.address || "New Jersey"}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              {(job.budget || job.budget_max || job.target_price) && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#d4a853]/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-[#d4a853]" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Budget</p>
                    <p className="font-semibold text-[#d4a853]">
                      ${job.budget || job.target_price || `${job.budget_min || 0} - ${job.budget_max}`}
                    </p>
                  </div>
                </div>
              )}
              
              {job.urgency && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Urgency</p>
                    <p className="font-semibold text-orange-600 capitalize">{job.urgency}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Description */}
          <div>
            <h3 className="font-semibold text-[#1e3a5f] mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Job Description
            </h3>
            <p className="text-slate-600 bg-slate-50 rounded-lg p-4">
              {job.description || "No description provided."}
            </p>
          </div>
          
          {/* Customer Info (if accepted) */}
          {isAccepted && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
              <h3 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                Customer Contact Information
              </h3>
              <div className="space-y-3">
                {job.customer_name && (
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-900">{job.customer_name}</span>
                  </div>
                )}
                {job.customer_phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-emerald-600" />
                    <a href={`tel:${job.customer_phone}`} className="text-emerald-700 hover:underline">
                      {job.customer_phone}
                    </a>
                  </div>
                )}
                {job.customer_email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-emerald-600" />
                    <a href={`mailto:${job.customer_email}`} className="text-emerald-700 hover:underline">
                      {job.customer_email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Submit Bid Section (for available leads) */}
          {!isAccepted && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <Send className="w-4 h-4" />
                Send a Quote / Bid
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">Your Bid Amount ($)</label>
                  <Input
                    type="number"
                    placeholder="e.g., 250"
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    className="border-blue-200 focus:border-blue-400"
                    data-testid="bid-amount-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">Message to Customer</label>
                  <Textarea
                    placeholder="Introduce yourself and explain why you're the right professional for this job..."
                    value={bidMessage}
                    onChange={(e) => setBidMessage(e.target.value)}
                    rows={4}
                    className="border-blue-200 focus:border-blue-400"
                    data-testid="bid-message-input"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Close
          </Button>
          
          {!isAccepted ? (
            <>
              <Button
                onClick={() => onAccept(job)}
                disabled={loading}
                className="flex-1 bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f] font-semibold"
                data-testid="modal-accept-lead"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accept Lead"}
              </Button>
              <Button
                onClick={handleSubmitBid}
                disabled={submitting || !bidAmount || !bidMessage}
                className="flex-1 bg-[#1e3a5f] hover:bg-[#152a45] text-white font-semibold"
                data-testid="submit-bid-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Bid
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              className="flex-1 bg-[#1e3a5f] hover:bg-[#152a45] text-white font-semibold"
              onClick={() => window.location.href = `tel:${job.customer_phone}`}
              disabled={!job.customer_phone}
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Customer
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ type }) => {
  const messages = {
    available: {
      icon: Inbox,
      title: "No new leads yet",
      description: "We'll notify you when new jobs match your services. Make sure your profile is complete and categories are set.",
    },
    accepted: {
      icon: CheckCircle2,
      title: "No accepted leads",
      description: "Leads you accept will appear here. Browse available leads to find new opportunities.",
    },
    all: {
      icon: Users,
      title: "No leads found",
      description: "There are no leads matching your search. Try adjusting your filters.",
    },
  };
  
  const msg = messages[type] || messages.all;
  const Icon = msg.icon;
  
  return (
    <div className="text-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
        <Icon className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">{msg.title}</h3>
      <p className="text-slate-500 max-w-md mx-auto">{msg.description}</p>
    </div>
  );
};

// Main Leads Page Component
export default function ProLeadsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("available");
  const [searchQuery, setSearchQuery] = useState("");
  const [availableLeads, setAvailableLeads] = useState([]);
  const [acceptedLeads, setAcceptedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch both available and accepted leads in parallel
      const [availableRes, acceptedRes] = await Promise.all([
        axios.get(`${API}/leads/assigned`, { headers }).catch(() => ({ data: { leads: [] } })),
        axios.get(`${API}/leads/accepted`, { headers }).catch(() => ({ data: { leads: [] } })),
      ]);
      
      // Filter available leads (not yet accepted)
      const accepted_job_ids = new Set(acceptedRes.data.leads?.map(l => l.job_id || l.job?.id) || []);
      const available = (availableRes.data.leads || []).filter(
        l => !accepted_job_ids.has(l.job_id || l.job?.id) && l.status !== "accepted"
      );
      
      setAvailableLeads(available);
      setAcceptedLeads(acceptedRes.data.leads || []);
      
      // Check if we should highlight a specific lead from push notification
      const highlightId = searchParams.get('highlight');
      if (highlightId) {
        const allLeads = [...available, ...(acceptedRes.data.leads || [])];
        const leadToHighlight = allLeads.find(l => (l.job?.id || l.id || l.job_id) === highlightId);
        if (leadToHighlight) {
          setSelectedLead(leadToHighlight);
        }
      }
    } catch (error) {
      console.error("Failed to fetch leads:", error);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [token, searchParams]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Accept a lead
  const handleAcceptLead = async (job) => {
    const leadId = job.id || job.job_id;
    setAcceptingId(leadId);
    
    try {
      await axios.put(
        `${API}/leads/${leadId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Lead accepted! You can now contact the customer.");
      
      // Refresh leads
      await fetchLeads();
      
      // Close modal and switch to accepted tab
      setSelectedLead(null);
      setActiveTab("accepted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept lead");
    } finally {
      setAcceptingId(null);
    }
  };

  // View lead details - opens modal
  const handleViewLead = (lead) => {
    setSelectedLead(lead);
  };

  // Filter leads by search
  const filterLeads = (leads) => {
    if (!searchQuery.trim()) return leads;
    const query = searchQuery.toLowerCase();
    return leads.filter(lead => {
      const job = lead.job || lead;
      return (
        job.title?.toLowerCase().includes(query) ||
        job.service_category?.toLowerCase().includes(query) ||
        job.category?.toLowerCase().includes(query) ||
        job.city?.toLowerCase().includes(query) ||
        job.location?.toLowerCase().includes(query) ||
        job.description?.toLowerCase().includes(query)
      );
    });
  };

  // Get current tab leads
  const getCurrentLeads = () => {
    switch (activeTab) {
      case "available":
        return filterLeads(availableLeads);
      case "accepted":
        return filterLeads(acceptedLeads);
      case "all":
        return filterLeads([...availableLeads, ...acceptedLeads]);
      default:
        return filterLeads(availableLeads);
    }
  };

  const currentLeads = getCurrentLeads();

  // First Lead Fee Info Banner
  const FirstLeadFeeBanner = () => (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="first-lead-fee-banner">
      <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm text-blue-800">
          <strong>Your first lead is free.</strong> After that, $29.99/month — cancel anytime.
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="pro-leads-page">
      {/* First Lead Fee Banner */}
      {!user?.first_lead_charged && <FirstLeadFeeBanner />}
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Leads</h1>
          <p className="text-slate-500">
            {availableLeads.length} available &bull; {acceptedLeads.length} accepted
          </p>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 border-slate-200"
            data-testid="leads-search-input"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-0">
        {[
          { id: "available", label: "Available", count: availableLeads.length },
          { id: "accepted", label: "Accepted", count: acceptedLeads.length },
          { id: "all", label: "All Leads", count: availableLeads.length + acceptedLeads.length },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-[#d4a853] text-[#1e3a5f]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === tab.id ? "bg-[#d4a853]/20 text-[#1e3a5f]" : "bg-slate-100 text-slate-500"
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-10 h-10 animate-spin text-[#d4a853] mx-auto mb-4" />
          <p className="text-slate-500">Loading leads...</p>
        </div>
      ) : currentLeads.length === 0 ? (
        <EmptyState type={activeTab} />
      ) : (
        /* Leads Grid */
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leads-grid">
          {currentLeads.map((lead, index) => (
            <LeadCard
              key={lead.id || lead.job?.id || `lead-${index}`}
              lead={lead}
              onAccept={handleAcceptLead}
              onView={handleViewLead}
              isAccepted={activeTab === "accepted" || lead.status === "accepted"}
              loading={acceptingId === (lead.job?.id || lead.id)}
            />
          ))}
        </div>
      )}
      
      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onAccept={handleAcceptLead}
          onRespond={fetchLeads}
          isAccepted={acceptedLeads.some(l => (l.job?.id || l.id) === (selectedLead.job?.id || selectedLead.id))}
          loading={acceptingId === (selectedLead.job?.id || selectedLead.id)}
        />
      )}
    </div>
  );
}
